/**
 * RTDB transport — replaces the WebSocket relay implemented in
 * server/wsServer.ts.
 *
 * Tree (see database.rules.json):
 *   /channels/{channelId}/members/{uid}        — presence; set on join, onDisconnect remove
 *   /channels/{channelId}/messages/{push}      — broadcast post_message stream
 *   /channels/{channelId}/permissions          — permissions blob
 *   /channels/{channelId}/talking/{uid}        — boolean
 *   /channels/{channelId}/activeApp            — currently active app id
 *   /signaling/{toUid}/{fromUid}/{push}        — webrtc offer/answer/ice (1:1)
 *   /presence/{uid}                            — global presence ts
 *
 * Public surface mirrors the relevant subset of MessageType in shared/shared.ts
 * so existing client modules can be ported with a 1-line transport swap.
 */
import {
	ref,
	query,
	onValue,
	onChildAdded,
	orderByKey,
	startAfter,
	limitToLast,
	push,
	set,
	update,
	remove,
	onDisconnect,
	get,
	serverTimestamp,
	DataSnapshot,
	Unsubscribe
} from 'firebase/database';
import { rtdb, firebaseAuth } from './firebase';

export interface SignalingMessage {
	type: 'webrtc_offer' | 'webrtc_answer' | 'webrtc_ice_candidate';
	payload: any;
	from: string;
	ts: number;
}

export interface ChannelMessage {
	type: string;
	from: string;
	ts: number;
	[key: string]: any;
}

export class FirebaseTransport {
	private channelId: string | null = null;
	private uid: string | null = null;
	private subs: Unsubscribe[] = [];

	get currentUid(): string | null {
		return this.uid;
	}

	get currentChannel(): string | null {
		return this.channelId;
	}

	/** Join a channel; sets presence with onDisconnect cleanup. */
	async joinChannel(channelId: string, profile: { name?: string; appId?: string } = {}): Promise<void> {
		const uid = firebaseAuth.currentUser?.uid;
		if (!uid) throw new Error('not signed in');
		this.uid = uid;
		this.channelId = channelId;

		const memberRef = ref(rtdb, `channels/${channelId}/members/${uid}`);
		await set(memberRef, {
			uid,
			name: profile.name || null,
			appId: profile.appId || null,
			joinedAt: serverTimestamp()
		});
		onDisconnect(memberRef).remove();

		// Also stamp global presence
		const presRef = ref(rtdb, `presence/${uid}`);
		await set(presRef, { online: true, ts: serverTimestamp(), channelId });
		onDisconnect(presRef).set({ online: false, ts: serverTimestamp() });
	}

	async leaveChannel(): Promise<void> {
		if (!this.channelId || !this.uid) return;
		await remove(ref(rtdb, `channels/${this.channelId}/members/${this.uid}`));
		await remove(ref(rtdb, `channels/${this.channelId}/talking/${this.uid}`));
		this.unsubscribeAll();
		this.channelId = null;
	}

	/** Subscribe to membership changes — called once after join. */
	onMembersChanged(cb: (members: Record<string, any>) => void): Unsubscribe {
		const r = ref(rtdb, `channels/${this.channelId}/members`);
		const unsub = onValue(r, (snap: DataSnapshot) => cb(snap.val() || {}));
		this.subs.push(unsub);
		return unsub;
	}

	/** Broadcast a post_message-style payload to all channel members. */
	async postMessage(message: any): Promise<void> {
		if (!this.channelId || !this.uid) throw new Error('not in a channel');
		const r = ref(rtdb, `channels/${this.channelId}/messages`);
		await push(r, { type: 'post_message', from: this.uid, ts: Date.now(), message });
	}

	/** Broadcast an arbitrary typed message (e.g. activate_app) to all channel members. */
	async broadcast(msg: { type: string; [k: string]: any }): Promise<void> {
		if (!this.channelId || !this.uid) throw new Error('not in a channel');
		const r = ref(rtdb, `channels/${this.channelId}/messages`);
		await push(r, { ...msg, from: this.uid, ts: Date.now() });
	}

	/**
	 * Subscribe to new channel messages. Uses the last existing push key as an
	 * anchor so only messages pushed after this subscription is established are
	 * delivered — no previous-session replay. Push keys are server-generated and
	 * lexicographically time-ordered, making this clock-skew-proof.
	 */
	async onMessage(cb: (msg: ChannelMessage) => void): Promise<Unsubscribe> {
		const r = ref(rtdb, `channels/${this.channelId}/messages`);
		const lastSnap = await get(query(r, orderByKey(), limitToLast(1)));
		let q;
		if (lastSnap.exists()) {
			let lastKey: string | null = null;
			lastSnap.forEach(child => { lastKey = child.key; });
			q = query(r, orderByKey(), startAfter(lastKey));
		} else {
			q = query(r, orderByKey());
		}
		const unsub = onChildAdded(q, snap => cb(snap.val() as ChannelMessage));
		this.subs.push(unsub);
		return unsub;
	}

	/** Set talking flag (presence-style; auto-cleared on disconnect). */
	async setTalking(talking: boolean): Promise<void> {
		if (!this.channelId || !this.uid) return;
		const r = ref(rtdb, `channels/${this.channelId}/talking/${this.uid}`);
		if (talking) {
			await set(r, true);
			onDisconnect(r).remove();
		} else {
			await remove(r);
		}
	}

	onTalkingChanged(cb: (talking: Record<string, boolean>) => void): Unsubscribe {
		const r = ref(rtdb, `channels/${this.channelId}/talking`);
		const unsub = onValue(r, snap => cb(snap.val() || {}));
		this.subs.push(unsub);
		return unsub;
	}

	async setActiveApp(appId: string): Promise<void> {
		await set(ref(rtdb, `channels/${this.channelId}/activeApp`), appId);
	}

	onActiveAppChanged(cb: (appId: string | null) => void): Unsubscribe {
		const r = ref(rtdb, `channels/${this.channelId}/activeApp`);
		const unsub = onValue(r, snap => cb((snap.val() as string) || null));
		this.subs.push(unsub);
		return unsub;
	}

	async updatePermissions(permissions: any): Promise<void> {
		await update(ref(rtdb, `channels/${this.channelId}/permissions`), permissions);
	}

	onPermissionsChanged(cb: (perms: any) => void): Unsubscribe {
		const r = ref(rtdb, `channels/${this.channelId}/permissions`);
		const unsub = onValue(r, snap => cb(snap.val() || {}));
		this.subs.push(unsub);
		return unsub;
	}

	/** Claim host role. Returns true if successful, false if another user already owns it. */
	async claimHost(): Promise<boolean> {
		if (!this.channelId || !this.uid) return false;
		try {
			await set(ref(rtdb, `channels/${this.channelId}/owner`), this.uid);
			return true;
		} catch {
			return false;
		}
	}

	async setRoomLocked(locked: boolean): Promise<void> {
		if (!this.channelId || !this.uid) return;
		if (locked) {
			await set(ref(rtdb, `channels/${this.channelId}/locked`), true);
		} else {
			await remove(ref(rtdb, `channels/${this.channelId}/locked`));
		}
	}

	async setAttention(signal: 'lost' | 'help' | null): Promise<void> {
		if (!this.channelId || !this.uid) return;
		const attRef = ref(rtdb, `channels/${this.channelId}/attention/${this.uid}`);
		if (signal) {
			await set(attRef, signal);
			onDisconnect(attRef).remove();
		} else {
			await remove(attRef);
		}
	}

	async clearAttentionForUser(userId: string): Promise<void> {
		if (!this.channelId) return;
		await remove(ref(rtdb, `channels/${this.channelId}/attention/${userId}`));
	}

	onOwnerChanged(cb: (ownerId: string | null) => void): Unsubscribe {
		const r = ref(rtdb, `channels/${this.channelId}/owner`);
		const unsub = onValue(r, snap => cb((snap.val() as string) || null));
		this.subs.push(unsub);
		return unsub;
	}

	onRoomLockedChanged(cb: (locked: boolean) => void): Unsubscribe {
		const r = ref(rtdb, `channels/${this.channelId}/locked`);
		const unsub = onValue(r, snap => cb(snap.val() === true));
		this.subs.push(unsub);
		return unsub;
	}

	onAttentionChanged(cb: (signals: Record<string, 'lost' | 'help'>) => void): Unsubscribe {
		const r = ref(rtdb, `channels/${this.channelId}/attention`);
		const unsub = onValue(r, snap => cb(snap.val() || {}));
		this.subs.push(unsub);
		return unsub;
	}

	// --- WebRTC signaling ---

	/** Send a signaling envelope to a specific peer. */
	async sendSignal(toUid: string, msg: Omit<SignalingMessage, 'from' | 'ts'>): Promise<void> {
		if (!this.uid) throw new Error('not signed in');
		await push(ref(rtdb, `signaling/${toUid}/${this.uid}`), {
			...msg,
			from: this.uid,
			ts: Date.now()
		});
	}

	/**
	 * Subscribe to incoming signals addressed to this uid. We watch for new
	 * `fromUid` buckets at /signaling/{uid}, and within each bucket watch for
	 * new messages (push children). Stale messages from prior sessions are
	 * cleared first to avoid replaying them through `onChildAdded`.
	 */
	onSignal(cb: (msg: SignalingMessage) => void): Unsubscribe {
		if (!this.uid) throw new Error('not signed in');
		const root = ref(rtdb, `signaling/${this.uid}`);
		// Best-effort: clear any stale messages left by a previous session.
		remove(root).catch(() => {/* ignore — likely no stale data */});

		const cutoff = Date.now();
		const innerUnsubs: Unsubscribe[] = [];

		const subscribeToBucket = (fromUid: string) => {
			const bucket = ref(rtdb, `signaling/${this.uid}/${fromUid}`);
			const u = onChildAdded(bucket, msgSnap => {
				const msg = msgSnap.val() as SignalingMessage;
				// Drop anything from before this session even if rules let it linger.
				if (!msg || (typeof msg.ts === 'number' && msg.ts < cutoff)) {
					remove(msgSnap.ref).catch(() => {});
					return;
				}
				try {
					cb(msg);
				} finally {
					remove(msgSnap.ref).catch(err =>
						console.warn('signal cleanup failed', err)
					);
				}
			});
			innerUnsubs.push(u);
		};

		const outerUnsub = onChildAdded(root, fromSnap => {
			subscribeToBucket(fromSnap.key as string);
		});

		const unsub: Unsubscribe = () => {
			outerUnsub();
			for (const u of innerUnsubs) {
				try { u(); } catch {}
			}
		};
		this.subs.push(unsub);
		return unsub;
	}

	private unsubscribeAll() {
		for (const u of this.subs) {
			try {
				u();
			} catch {}
		}
		this.subs = [];
	}
}

export const transport = new FirebaseTransport();
